import {useCallback, useState} from 'react';

import {useSelector} from 'react-redux';

import {AddressPartsFromKey} from '~/shared/utils/address';
import {
  selectCurrentDeliveryMethod,
} from '~/shared/store/storeModules/currentDeliveryMethod/currentDeliveryMethodSelectors';
import {DeliveryMethod, DeliveryMethods} from '~/shared/consts/restaurantConsts';
import {Address, LocalAddress} from '~/shared/store/models';
import {createLogger} from '~/shared/logging';
import ManagerProvider from '~/shared/managers/ManagerProvider';
import {navigateToRestaurantsPage} from '~/shared/services/navigation';
import {selectCurrentModal} from '~/shared/store/storeModules/currentModal/currentModalSelectors';
import {selectUser} from '~/shared/store/storeModules/user';

const logger = createLogger('useHomePageAddressSearch');

const useHomePageAddressSearch = ({onChangeAddressRequestStart, onChangeAddressRequestFinish}: {
  onChangeAddressRequestStart?: () => void;
  onChangeAddressRequestFinish?: () => void;
} = {}) => {
  const [disableSubmit, setDisableSubmit] = useState(true);
  const currentModal = useSelector(selectCurrentModal);
  const currentUser = useSelector(selectUser);
  const disableAutoFocus = Boolean(!currentUser?.data || currentModal);
  const initialDeliveryMethod = useSelector(selectCurrentDeliveryMethod) || DeliveryMethods.DELIVERY;
  const [deliveryMethod, setDeliveryMethod] = useState(initialDeliveryMethod);
  const [address, setAddress] = useState<Address | LocalAddress | AddressPartsFromKey>();

  const onNewAddressSubmit = async (newAddress?: Address | LocalAddress | AddressPartsFromKey) => {
    if (!newAddress && !address) {
      return;
    }

    const finalAddress = newAddress || address;

    try {
      if (onChangeAddressRequestStart) {
        onChangeAddressRequestStart();
      }
      const changeDeliveryMethodPromise =
        deliveryMethod !== initialDeliveryMethod
          ? ManagerProvider.changeDeliveryMethod({deliveryMethod, searchRestaurants: false})
          : Promise.resolve();

      await Promise.all([changeDeliveryMethodPromise, ManagerProvider.changeAddress({address: finalAddress})]);
      navigateToRestaurantsPage();
    } catch (error) {
      logger.error(new Error('failed to add initial address'), {error, address: finalAddress});
    } finally {
      if (onChangeAddressRequestFinish) {
        onChangeAddressRequestFinish();
      }
    }
  };

  const onDeliveryMethodChange = useCallback((val: DeliveryMethod) => setDeliveryMethod(val), []);

  const onAddressFinderChange = useCallback((newAddress?: Address | LocalAddress | AddressPartsFromKey) => {
    if (newAddress) {
      setAddress(newAddress);
    }
  }, []);

  return {
    disableAutoFocus,
    deliveryMethod,
    disableSubmit,
    setDisableSubmit,
    onNewAddressSubmit,
    onDeliveryMethodChange,
    onAddressFinderChange,
  };
};

export default useHomePageAddressSearch;
